import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowLeft, Crown, BadgeCheck, Ban } from 'lucide-react';

export default function PremiumSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { user, updateUser } = useAuth();
  
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/premium/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          updateUser({ is_premium: true, is_verified: true });
        } else if (response.data.payment_status === 'expired' || response.data.payment_status === 'failed') {
          setStatus('failed');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="glass-card p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
            <h1 className="font-heading text-2xl font-bold mb-2">Activating Premium</h1>
            <p className="text-muted-foreground">Please wait while we upgrade your account...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center">
              <Crown className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Welcome to Premium!</h1>
            <p className="text-muted-foreground mb-6">
              Your account has been upgraded. Enjoy your new benefits!
            </p>
            
            <div className="space-y-3 mb-6 text-left">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                <Ban className="w-5 h-5 text-green-500" />
                <span>Ad-free experience</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                <BadgeCheck className="w-5 h-5 text-primary" />
                <span>Verified badge on your profile</span>
              </div>
            </div>
            
            <Link to="/home">
              <Button className="rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Feed
              </Button>
            </Link>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h1 className="font-heading text-2xl font-bold mb-2">Upgrade Failed</h1>
            <p className="text-muted-foreground mb-4">
              Unfortunately, your payment could not be processed.
            </p>
            <Link to="/settings">
              <Button variant="outline" className="rounded-full">
                Try Again
              </Button>
            </Link>
          </>
        )}

        {(status === 'pending' || status === 'error') && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
            <h1 className="font-heading text-2xl font-bold mb-2">
              {status === 'pending' ? 'Payment Pending' : 'Something Went Wrong'}
            </h1>
            <p className="text-muted-foreground mb-4">
              Please check your account settings for status updates.
            </p>
            <Link to="/settings">
              <Button variant="outline" className="rounded-full">
                Go to Settings
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
